// src/components/Schedule1AInputs.js
import React, { useEffect } from 'react';
import { DollarSign, Clock, Truck, UserPlus, HelpCircle, AlertCircle, CheckCircle } from 'react-feather';

function Schedule1AInputs({ formData, onDataChange }) {
  const { filingStatus } = formData;
  const isSenior = formData.isOver65;
  const isSpouseSenior = formData.spouseIsOver65;
  
  // Calculate potential Senior Bonus for display
  let seniorBonusAmount = 0;
  if (isSenior) seniorBonusAmount += 6000;
  if (filingStatus.includes('Married') && isSpouseSenior) seniorBonusAmount += 6000;

  // --- AUTO-OPEN LOGIC ---
  // If the user is a Senior, they automatically qualify. 
  // We force the toggle to "true" if it hasn't been set yet.
  useEffect(() => {
    if ((isSenior || isSpouseSenior) && formData.hasSchedule1A !== true) {
      onDataChange({
        target: { name: 'hasSchedule1A', value: true, type: 'radio' }
      });
    }
  }, [isSenior, isSpouseSenior, formData.hasSchedule1A, onDataChange]);

  // Helper for the Yes/No toggle
  const handleToggleChange = (e) => {
    const { name, value } = e.target;
    onDataChange({
      target: { name, value: value === 'true', type: 'radio' }
    });
  };

  return (
    <div className="form-section" style={{ marginTop: '2rem' }}>
      <div className="content-box-header">
        <h2><DollarSign size={20} className="icon-orange" /> New 2025 Deductions</h2>
      </div>

      {/* --- QUALIFICATION QUESTION --- */}
      <div className="dependent-card" style={{ marginBottom: '1.5rem', border: '1px solid #fed7aa', backgroundColor: '#fff7ed' }}>
        <div className="form-group">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <AlertCircle size={20} className="icon-orange" />
            <label style={{ fontWeight: 600, color: '#9a3412', fontSize: '1.05rem', margin: 0 }}>
              Do you qualify for the new "Below-the-Line" Deductions?
            </label>
          </div>
          
          <p style={{ fontSize: '0.9rem', color: '#c2410c', marginBottom: '1rem', lineHeight: '1.5' }}>
            The 2025 tax law added specific deductions that reduce your taxable income. 
            <strong> Do any of the following apply to you?</strong>
          </p>

          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#7c2d12' }}>
              <CheckCircle size={16} /> You work for tips (Waiter, etc.)
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#7c2d12' }}>
              <CheckCircle size={16} /> You received hourly overtime pay
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#7c2d12' }}>
              <CheckCircle size={16} /> You bought a new US-made car
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#7c2d12' }}>
              <CheckCircle size={16} /> You or your spouse are age 65+
            </li>
          </ul>

          <div className="radio-group">
            <input 
              type="radio" 
              id="s1a_no" 
              name="hasSchedule1A" 
              value="false" 
              checked={formData.hasSchedule1A === false} 
              onChange={handleToggleChange} 
            />
            <label htmlFor="s1a_no">No</label>

            <input 
              type="radio" 
              id="s1a_yes" 
              name="hasSchedule1A" 
              value="true" 
              checked={formData.hasSchedule1A === true} 
              onChange={handleToggleChange} 
            />
            <label htmlFor="s1a_yes">Yes</label>
          </div>
        </div>
      </div>

      {/* --- CONDITIONAL FORM RENDERING --- */}
      {formData.hasSchedule1A && (
        <div style={{ animation: 'fadeIn 0.3s' }}>
          
          {/* --- PART I: MAGI ADJUSTMENTS --- */}
          <div className="dependent-card" style={{ marginBottom: '1.5rem' }}>
            <div className="form-group" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px dashed #e5e7eb' }}>
              <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: 600, color: '#554333ff' }}>Part I: MAGI Adjustments</label>
                <div className="has-tooltip header-help-icon" data-tooltip="Income excluded from your main return that must be added back to calculate eligibility.">
                  <HelpCircle size={16} />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Puerto Rico Income (Excluded)</label>
                  <input 
                    type="number" 
                    name="puertoRicoIncome" 
                    value={formData.puertoRicoIncome ?? ''} 
                    onChange={onDataChange} 
                    placeholder="Line 2a" 
                  />
                </div>
                <div className="form-group">
                  <label>Foreign Housing Exclusion</label>
                  <input 
                    type="number" 
                    name="foreignHousingExclusion" 
                    value={formData.foreignHousingExclusion ?? ''} 
                    onChange={onDataChange} 
                    placeholder="Form 2555, Line 50" 
                  />
                  <small>Line 2c</small>
                </div>
                <div className="form-group">
                  <label>Possession Exclusion (Form 4563)</label>
                  <input 
                    type="number" 
                    name="possessionExclusion" 
                    value={formData.possessionExclusion ?? ''} 
                    onChange={onDataChange} 
                    placeholder="Line 2d" 
                  />
                </div>
              </div>
              <small style={{ color: '#8b7b64ff' }}>Note: Foreign Earned Income (Form 2555) is pulled automatically from the "Other Income" tab.</small>
            </div>

            {/* --- 1. NO TAX ON TIPS (Part II) --- */}
            <div className="form-group" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px dashed #e5e7eb' }}>
              <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <DollarSign size={18} className="icon-blue" />
                  <label style={{ fontWeight: 600, color: '#554333ff' }}>Part II: No Tax on Tips</label>
                </div>
                <div className="has-tooltip header-help-icon" data-tooltip="Only tips from 'customarily tipped occupations' qualify. See IRS.gov/TippedOccupations.">
                  <HelpCircle size={16} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>W-2 Tips (Box 7)</label>
                  <input 
                    type="number" 
                    name="tipsW2" 
                    value={formData.tipsW2 ?? ''} 
                    onChange={onDataChange} 
                    placeholder="Line 4a" 
                  />
                </div>
                <div className="form-group">
                  <label>Unreported Tips (Form 4137)</label>
                  <input 
                    type="number" 
                    name="tips4137" 
                    value={formData.tips4137 ?? ''} 
                    onChange={onDataChange} 
                    placeholder="Line 4b" 
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Self-Employed Tips</label>
                <input 
                  type="number" 
                  name="tipsSelfEmployed" 
                  value={formData.tipsSelfEmployed ?? ''} 
                  onChange={onDataChange} 
                  placeholder="Line 5 (1099-NEC/K)" 
                />
                <small>Tips received in the course of a trade or business.</small>
              </div>
            </div>

            {/* --- 2. NO TAX ON OVERTIME (Part III) --- */}
            <div className="form-group" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px dashed #e5e7eb' }}>
              <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Clock size={18} className="icon-blue" />
                  <label style={{ fontWeight: 600, color: '#554333ff' }}>Part III: No Tax on Overtime</label>
                </div>
                <div className="has-tooltip header-help-icon" data-tooltip="Compensation for hours worked in excess of 40 hours in a week (at least 1.5x regular rate).">
                  <HelpCircle size={16} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>W-2 Overtime (Line 14a)</label>
                  <input 
                    type="number" 
                    name="overtimeW2" 
                    value={formData.overtimeW2 ?? ''} 
                    onChange={onDataChange} 
                    placeholder="Employees" 
                  />
                </div>
                <div className="form-group">
                  <label>1099 Overtime (Line 14b)</label>
                  <input 
                    type="number" 
                    name="overtime1099" 
                    value={formData.overtime1099 ?? ''} 
                    onChange={onDataChange} 
                    placeholder="Contractors" 
                  />
                </div>
              </div>
              <small style={{ display: 'block', marginTop: '5px', color: '#8b7b64ff' }}>
                Deduction is capped at $12,500 ($25,000 for Married Filing Jointly).
              </small>
            </div>

            {/* --- 3. CAR LOAN INTEREST (Part IV) --- */}
            <div className="form-group" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px dashed #e5e7eb' }}>
              <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Truck size={18} className="icon-blue" />
                  <label style={{ fontWeight: 600, color: '#554333ff' }}>Part IV: Car Loan Interest</label>
                </div>
                <div className="has-tooltip header-help-icon" data-tooltip="Interest paid on loans for qualified US-assembled vehicles. You cannot claim interest already deducted as a business expense.">
                  <HelpCircle size={16} />
                </div>
              </div>

              {/* Vehicle 1 */}
              <div style={{ backgroundColor: '#f9fafb', padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '10px' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>Vehicle 1 (Line 22a)</h5>
                <div className="form-group">
                    <label>VIN</label>
                    <input type="text" name="car1_vin" value={formData.car1_vin ?? ''} onChange={onDataChange} placeholder="17-character VIN" />
                </div>
                <div className="form-row" style={{ marginTop: '0.5rem' }}>
                    <div className="form-group">
                        <label>Total Interest Paid</label>
                        <input type="number" name="car1_totalInterest" value={formData.car1_totalInterest ?? ''} onChange={onDataChange} />
                    </div>
                    <div className="form-group">
                        <label>Business Portion (Sch C/E)</label>
                        <input type="number" name="car1_businessDeduction" value={formData.car1_businessDeduction ?? ''} onChange={onDataChange} />
                    </div>
                </div>
              </div>

              {/* Vehicle 2 */}
              <div style={{ backgroundColor: '#f9fafb', padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>Vehicle 2 (Line 22b)</h5>
                <div className="form-group">
                    <label>VIN</label>
                    <input type="text" name="car2_vin" value={formData.car2_vin ?? ''} onChange={onDataChange} placeholder="Optional" />
                </div>
                <div className="form-row" style={{ marginTop: '0.5rem' }}>
                    <div className="form-group">
                        <label>Total Interest Paid</label>
                        <input type="number" name="car2_totalInterest" value={formData.car2_totalInterest ?? ''} onChange={onDataChange} />
                    </div>
                    <div className="form-group">
                        <label>Business Portion (Sch C/E)</label>
                        <input type="number" name="car2_businessDeduction" value={formData.car2_businessDeduction ?? ''} onChange={onDataChange} />
                    </div>
                </div>
              </div>

              <small style={{ display: 'block', marginTop: '10px', color: '#8b7b64ff' }}>
                Net deduction is capped at $10,000.
              </small>
            </div>

            {/* --- 4. SENIOR BONUS (Read Only) --- */}
            <div className="form-group">
              <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <UserPlus size={18} className="icon-blue" />
                  <label style={{ fontWeight: 600, color: '#554333ff' }}>Senior Bonus Deduction</label>
                </div>
                <div className="has-tooltip header-help-icon" data-tooltip="Additional $6,000 deduction for each taxpayer age 65 or older.">
                  <HelpCircle size={16} />
                </div>
              </div>
              
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#f0fdf4', 
                border: '1px solid #86efac', 
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: '#166534' }}>
                  Based on age ({isSenior ? 'You' : ''}{isSenior && isSpouseSenior ? ' & ' : ''}{isSpouseSenior ? 'Spouse' : ''}{!isSenior && !isSpouseSenior ? 'None' : ''}):
                </span>
                <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#15803d' }}>
                  ${seniorBonusAmount.toLocaleString()}
                </span>
              </div>
              {seniorBonusAmount > 0 && (
                 <small style={{ display: 'block', marginTop: '5px', color: '#166534' }}>
                   This is automatically calculated based on the birth dates entered in Personal Info.
                 </small>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Schedule1AInputs;